import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Printer,
  Mail,
  RefreshCcw,
  FileBarChart,
  Table,
  Filter,
  Calendar,
  Loader2
} from 'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const formatValue = (value, type) => {
  if (value === null || value === undefined) return '-';
  
  switch (type) {
    case 'currency':
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    case 'date':
      return new Date(value).toLocaleDateString('pt-BR');
    case 'number':
      return new Intl.NumberFormat('pt-BR').format(value);
    default:
      return value;
  }
};

export const ReportPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [report, setReport] = useState(null);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [executedAt, setExecutedAt] = useState(null);

  useEffect(() => {
    fetchReportAndExecute();
  }, [id]);

  const fetchReportAndExecute = async () => {
    setLoading(true);
    try {
      // Fetch report details
      const reportRes = await fetch(`${API_URL}/api/report-builder/reports/${id}`, {
        headers: getAuthHeaders()
      });
      
      if (!reportRes.ok) {
        toast.error('Relatório não encontrado');
        navigate('/reports');
        return;
      }
      
      const reportData = await reportRes.json();
      setReport(reportData);
      setColumns(reportData.selected_columns || []);

      // Execute report
      await executeReport();
    } catch (err) {
      console.error('Error:', err);
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  const executeReport = async () => {
    setExecuting(true);
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${id}/execute`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          filters: {},
          output_format: 'json'
        })
      });

      if (res.ok) {
        const result = await res.json();
        setData(result.data || []);
        setTotalRows(result.total_rows || 0);
        setExecutedAt(result.executed_at);
        setColumns(result.columns || []);
      } else {
        toast.error('Erro ao executar relatório');
      }
    } catch (err) {
      toast.error('Erro ao executar relatório');
    } finally {
      setExecuting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/reports/${id}/export/${format}`, {
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        toast.error(`Erro ao exportar ${format.toUpperCase()}`);
        return;
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] || `${report?.name || 'relatorio'}.${format === 'excel' ? 'xlsx' : format}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportação ${format.toUpperCase()} gerada`);
    } catch (err) {
      toast.error(`Erro ao exportar ${format.toUpperCase()}`);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    toast.info('Envio por email será implementado em breve');
  };

  // Calculate totals for summable columns
  const calculateTotals = () => {
    const totals = {};
    columns.forEach(col => {
      if (col.is_summable && col.field_type === 'currency' || col.field_type === 'number') {
        totals[col.field_key] = data.reduce((sum, row) => sum + (parseFloat(row[col.field_key]) || 0), 0);
      }
    });
    return totals;
  };

  const totals = report?.show_totals ? calculateTotals() : {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-preview">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Space_Grotesk']">
              {report?.name || 'Relatório'}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline">{report?.type_name}</Badge>
              {executedAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(executedAt).toLocaleString('pt-BR')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={executeReport} disabled={executing}>
            {executing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4 mr-2" />
            )}
            Atualizar
          </Button>
          <Button variant="outline" onClick={() => handleExport('excel')}>
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')}>
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Imprimir
          </Button>
          <Button variant="outline" onClick={handleEmail}>
            <Mail className="h-4 w-4 mr-2" />
            Enviar
          </Button>
        </div>
      </div>

      {/* Report Info - Print Header */}
      <div className="hidden print:block mb-4">
        <h1 className="text-2xl font-bold">{report?.name}</h1>
        <p className="text-sm text-gray-600">
          Gerado em: {executedAt ? new Date(executedAt).toLocaleString('pt-BR') : '-'}
        </p>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-3 print:hidden">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Table className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total de Registros</p>
                <p className="text-2xl font-bold">{totalRows}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileBarChart className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Colunas</p>
                <p className="text-2xl font-bold">{columns.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Filter className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Filtros Aplicados</p>
                <p className="text-2xl font-bold">0</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card className="card-elevated print:shadow-none print:border-0">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  {columns.map((col) => (
                    <th 
                      key={col.field_key} 
                      className={`py-3 px-4 font-medium text-muted-foreground ${
                        col.field_type === 'number' || col.field_type === 'currency' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {col.field_label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-muted-foreground">
                      Nenhum registro encontrado
                    </td>
                  </tr>
                ) : (
                  data.map((row, rowIndex) => (
                    <tr 
                      key={rowIndex} 
                      className={`border-b last:border-0 ${rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                    >
                      {columns.map((col) => (
                        <td 
                          key={col.field_key}
                          className={`py-3 px-4 ${
                            col.field_type === 'number' || col.field_type === 'currency' ? 'text-right' : 'text-left'
                          }`}
                        >
                          {formatValue(row[col.field_key], col.field_type)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {report?.show_totals && Object.keys(totals).length > 0 && (
                <tfoot>
                  <tr className="border-t-2 bg-muted/50 font-semibold">
                    {columns.map((col, index) => (
                      <td 
                        key={col.field_key}
                        className={`py-3 px-4 ${
                          col.field_type === 'number' || col.field_type === 'currency' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {index === 0 ? 'TOTAL' : totals[col.field_key] !== undefined 
                          ? formatValue(totals[col.field_key], col.field_type)
                          : ''
                        }
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Print Footer */}
      <div className="hidden print:block mt-4 text-sm text-gray-500 text-center">
        <p>Relatório gerado pelo EzERP Report Builder</p>
      </div>
    </div>
  );
};

export default ReportPreview;
